/**
 * The ONE reading of a lift car: how fresh it is, what it means for
 * guests and for robots, who acts, and the site row it adds up to.
 *
 * Twin slice 1, W6 step 5. The drawer, the attention cards and both
 * renderers are to read a car through this file (the drawer lines and
 * the columns land later in W6), so the Guests line a person sees in a
 * card is the same string the drawer shows. W5 maps these readings to
 * attention items (evidence, debounce, folding) and writes no lift text
 * of its own (plan section 4.5, X3).
 *
 * WHAT THIS FILE NEVER DOES:
 *
 * - Subtract another machine's clock from this one. A car's freshness is
 *   measured from THIS PAGE's own receipts of a row whose stamp
 *   advanced (`noteLiftReceipt`), never `Date.now() - lastSeen`:
 *   `lastSeen` is the worker's `updated_at_ms`, and any skew between the
 *   worker and this device would make a dead car look live or a live one
 *   dead (`rmf-client.ts::adaptV1LiftState`).
 * - Show the adapter's wall-clock stamps as an age or a countdown. They
 *   are shown as "since HH:MM" only (risk 14), with the day when it is
 *   not today; this device's wall clock is read only to name that day.
 * - Read `refusal.since_ts` as a "since". It moves on every repeat of the
 *   refusal (the adapter's 60 s self-clear compares against it); the
 *   start a person sees is `refusal.first_since_epoch_ts`.
 * - Raise an item for a guest car. A car robots are not meant to use is
 *   building information only (Decision 3).
 * - Say "Robots can't change floors" while the robot map still gives a
 *   car robots are not meant to use cabin lanes (Q1). The facts come in
 *   as data (`LiftRobotFacts`), so the wording flips by itself once a
 *   map drop removes the lanes and the asset is re-exported.
 *
 * Copy here is pending the slice-1 copy review (gate B).
 */
import type { LiftViewModel } from '../../api/adapters';
import type { Audience } from '../../auth/roles';
import type { LiftSessionView, LiftWriteGate } from '../../api/v1';
import { LOST_AFTER_MS, QUIET_AFTER_MS, classify } from '../../state/liveness';
import { browserTimeZone, isKnownTimeZone, zonedParts } from '../Tasks/zoned-time';
import { liftDispatchStatus, writeGateStatus } from './lift-dispatch-status';
import {
  liftModeGuestsLine,
  readLiftModeLabel,
  type LiftModeLabelReading,
} from './lift-mode-label';

// ----------------------------------------------------------------------------
// Receipts: this page's own record of when a car's row advanced.
// ----------------------------------------------------------------------------

/**
 * What this page has seen of one car's row. Built only by
 * `noteLiftReceipt`. Whoever holds them must drop them on reconnect and
 * on wake, as W3's robot receipts reset, so a replayed row starts again
 * at "checking"; that holder (the column state hook) is not this file.
 */
export interface LiftReceipt {
  /** The row's own stamp (`updated_at_ms`, carried as `lastSeen`). Only
   *  ever compared with itself, to tell whether the row advanced. */
  readonly stampMs: number;
  /** THIS DEVICE's clock (the caller's monotonic source, such as
   *  `performance.now()`) when the stamp was first seen or last advanced. */
  readonly atMs: number;
  /** How many times the stamp has strictly advanced while this page
   *  watched. 0 means the row has been seen once: "checking". */
  readonly advances: number;
}

/** The row's own stamp in epoch ms, or NaN when it has none. */
export function liftRowStampMs(row: Pick<LiftViewModel, 'lastSeen'> | undefined): number {
  return row?.lastSeen ? Date.parse(row.lastSeen) : Number.NaN;
}

/**
 * The words for a car with no reading this page trusts (copy deck v2, N9):
 * ONE set, read by the drawer and the cards (`liftReading`), the building
 * view's captions and the Devices Reading column (`liftLiveLabel`), so one
 * state is never "No reading since 14:40" in one place and "No signal ·
 * last 14:40" in another.
 */
export const LIFT_NO_READING_WORDS = {
  noReading: 'No reading',
  noFreshReading: 'No fresh reading',
  /** This page's own feed is clamped: says nothing about the lift. */
  reconnecting: 'Not known: this screen is reconnecting',
} as const;

/**
 * The line for a car with no trustworthy reading, by why:
 *
 *   feed_clamped                 "Not known: this screen is reconnecting"
 *   telemetry_stale, reading_age "No fresh reading" -- never a time: the
 *                                worker keeps stamping the row, so its
 *                                stamp is not when the reading went
 *   anything else                "No reading since 14:40" while the row's
 *                                own stamp IS the last reading (receipts
 *                                stopped), else "No reading"
 *
 * `clock` turns an epoch-ms stamp into a time of day (`formatClock`).
 */
export function liftNoReadingLine(
  lostBecause: LiftLostReason | null,
  row: Pick<LiftViewModel, 'lastSeen'> | undefined,
  clock: (epochMs: number) => string,
): string {
  const w = LIFT_NO_READING_WORDS;
  if (lostBecause === 'feed_clamped') return w.reconnecting;
  if (lostBecause === 'telemetry_stale' || lostBecause === 'reading_age') return w.noFreshReading;
  const stamp = lostBecause === 'no_receipts' ? liftRowStampMs(row) : Number.NaN;
  return Number.isFinite(stamp) ? `${w.noReading} since ${clock(stamp)}` : w.noReading;
}

/**
 * Note one receipt of a car's row at `atMs` on this device's clock.
 *
 * Counts a receipt only when the stamp is STRICTLY newer: an SSE replay
 * of the same document, a REST seed of a row the stream already sent,
 * or an out-of-order older sample changes nothing, and the same object
 * comes back so a parked car causes no re-render. A non-finite stamp is
 * no evidence at all and is ignored.
 */
export function noteLiftReceipt(
  prev: LiftReceipt | undefined,
  stampMs: number,
  atMs: number,
): LiftReceipt | undefined {
  if (!Number.isFinite(stampMs)) return prev;
  if (!prev) return { stampMs, atMs, advances: 0 };
  if (stampMs <= prev.stampMs) return prev;
  return { stampMs, atMs, advances: prev.advances + 1 };
}

// ----------------------------------------------------------------------------
// Freshness.
// ----------------------------------------------------------------------------

export type LiftCarFreshness =
  /** The row advanced within 10 s, as this page saw it, at least twice. */
  | 'live'
  /** It last advanced 10 to 30 s ago. Dimmed, never solid. */
  | 'quiet'
  /** Seen once and not yet advanced: a REST seed or a replay is not a
   *  live car until a newer row arrives. Never solid. */
  | 'checking'
  /** No trustworthy reading: see `LiftLostReason`. */
  | 'lost'
  /** The car reports mode off. */
  | 'off'
  /** No row at all, or only the building map's registry entry. */
  | 'absent';

export type LiftLostReason =
  /** This page's feed is clamped (W3's FeedHealth): nothing it holds is
   *  current, this car included. */
  | 'feed_clamped'
  /** The connector says its own telemetry is not fresh. */
  | 'telemetry_stale'
  /** The adapter has not received a connector document for 30 s or more. */
  | 'reading_age'
  /** This page has not seen the row advance for 30 s or more, or has no
   *  receipt to measure from. */
  | 'no_receipts';

export interface LiftFreshness {
  readonly state: LiftCarFreshness;
  /** Why it is lost; null for every other state. */
  readonly lostBecause: LiftLostReason | null;
  /**
   * How recently the row advanced, for a reading this page trusts: live,
   * quiet and off. Null for absent, lost and checking. `off` says what the
   * car reports, not how fresh that is, so an off car 10 to 30 s after its
   * last advance carries `quiet` here and is drawn dimmed like any quiet
   * car: on a disarmed site every car is off, and a stalled feed must
   * still show.
   */
  readonly liveness: 'live' | 'quiet' | null;
}

export interface LiftFreshnessInput {
  readonly row: Pick<LiftViewModel, 'mode' | 'hasLiveState' | 'session'> | undefined;
  readonly receipt: LiftReceipt | undefined;
  /** This device's clock, the same source the receipt's `atMs` came from. */
  readonly nowMs: number;
  /** W3's FeedHealth, or anything with its `clamped` flag. */
  readonly feed?: { readonly clamped: boolean } | null;
}

const LOST_AFTER_S = LOST_AFTER_MS / 1000;

/**
 * How fresh one car's reading is, on this device's clock.
 *
 * Precedence: absent, then lost (feed clamped, telemetry stale, reading
 * age 30 s or more, receipts stopped), then checking, then off, then
 * live or quiet. Lost outranks off on purpose: a row that has stopped
 * advancing is no evidence the car is still off. Off still carries its
 * liveness, so a quiet off car dims.
 *
 * `reading_age_s` and `telemetry_fresh` are the adapter's and the
 * connector's own measures, each on one clock, so they are read as
 * given; null means "not reported" and is never read as fresh or stale.
 */
export function liftCarFreshness(input: LiftFreshnessInput): LiftFreshness {
  const { row, receipt, nowMs, feed } = input;
  if (!row || !row.hasLiveState) return { state: 'absent', lostBecause: null, liveness: null };
  const lost = (lostBecause: LiftLostReason): LiftFreshness => ({ state: 'lost', lostBecause, liveness: null });
  if (feed?.clamped === true) return lost('feed_clamped');
  const session = row.session;
  if (session?.telemetry_fresh === false) return lost('telemetry_stale');
  const readingAge = session?.reading_age_s;
  // `!(age < 30)` rather than `age >= 30`, so a NaN reads lost too.
  if (typeof readingAge === 'number' && !(readingAge < LOST_AFTER_S)) return lost('reading_age');
  if (!receipt) return lost('no_receipts');
  const liveness = classify(nowMs - receipt.atMs);
  if (liveness === 'lost') return lost('no_receipts');
  if (receipt.advances === 0) return { state: 'checking', lostBecause: null, liveness: null };
  if (row.mode === 'off') return { state: 'off', lostBecause: null, liveness };
  return { state: liveness, lostBecause: null, liveness };
}

/**
 * The caveat a car's freshness carries when the adapter reports neither
 * of its own measures (`telemetry_fresh`, `reading_age_s`): an adapter
 * older than lift-session 0.1.18, or one rolled back. Freshness then
 * rests on this page's receipts alone, and those show only that the
 * worker is still publishing the row, not that the controller reading
 * behind it is current. Null when either measure is reported, or when
 * there is no row to caveat.
 */
export function liftFreshnessCaveat(
  row: Pick<LiftViewModel, 'hasLiveState' | 'session'> | undefined,
): string | null {
  if (!row || !row.hasLiveState) return null;
  const session = row.session;
  if (session?.telemetry_fresh != null || session?.reading_age_s != null) return null;
  return LIFT_FRESHNESS_CAVEAT;
}

/** The freshness caveat, as ops (and the drawer's ops-only line) read it. */
export const LIFT_FRESHNESS_CAVEAT =
  'The lift adapter does not report how fresh its reading is: this shows only that the platform is still publishing it.';
/** The same caveat for a SITE audience (copy deck v2, N8): what it means
 *  for the reader, with no "lift adapter" and no "platform". */
export const LIFT_FRESHNESS_CAVEAT_SITE =
  'The lifts don’t report how old their readings are, so a reading may be older than it looks.';

/**
 * A car's freshness caveat (`LiftColumnState.caveat`, from
 * `liftFreshnessCaveat`) in this reader's words: ops read it as given; a
 * site audience reads `LIFT_FRESHNESS_CAVEAT_SITE`. Null stays null: the
 * caveat is said only while a car's row lacks both measures.
 */
export function liftFreshnessCaveatFor(caveat: string | null, audience: Audience): string | null {
  if (caveat === null) return null;
  return audience === 'ops' ? caveat : LIFT_FRESHNESS_CAVEAT_SITE;
}

/**
 * Milliseconds until `liftCarFreshness` would change for this car with
 * no new receipt, for a boundary timer. Null when only a new receipt can
 * change it. A checking car has one boundary (lost at 30 s); a live one
 * two (quiet at 10 s, lost at 30 s).
 */
export function liftFreshnessNextChangeMs(
  receipt: LiftReceipt | undefined,
  nowMs: number,
): number | null {
  if (!receipt) return null;
  const age = nowMs - receipt.atMs;
  const liveness = classify(age);
  if (liveness === 'lost') return null;
  if (receipt.advances === 0 || liveness === 'quiet') return LOST_AFTER_MS - age;
  return QUIET_AFTER_MS - age;
}

// ----------------------------------------------------------------------------
// Which cars robots use: building facts, passed in as data.
// ----------------------------------------------------------------------------

/** The asset's `robot.use` (W1 overlay): SL1 between floors, PL3 on one
 *  floor, every other NoMad car none (Decision 2). */
export type LiftRobotUse = 'between_floors' | 'same_floor' | 'none';

export interface LiftRobotFacts {
  /** The car's name as the controller roster and the live rows spell it. */
  readonly name: string;
  readonly use: LiftRobotUse;
  /**
   * For a `none` car: the floors on which the served robot map still
   * gives it cabin lanes, so RMF can still route a robot through it.
   * Absent or empty when the map agrees with `use`.
   */
  readonly mapLaneLevels?: readonly string[] | null;
  /** The overlay's own words for the same fact (`robot.map_note`). Its
   *  presence counts as lanes even when no floors are listed. */
  readonly mapNote?: string | null;
}

/** True when the robot map still routes robots through a car robots are
 *  not meant to use (Q1). */
export function hasRobotMapLanes(facts: LiftRobotFacts): boolean {
  if (facts.use !== 'none') return false;
  if ((facts.mapLaneLevels?.length ?? 0) > 0) return true;
  return typeof facts.mapNote === 'string' && facts.mapNote.trim() !== '';
}

/** "FL1 (L3, L4, L6)", or the overlay's own words when no floors are listed. */
function mapLanesPhrase(facts: LiftRobotFacts): string {
  const levels = facts.mapLaneLevels ?? [];
  if (levels.length > 0) return `${facts.name} (${levels.join(', ')})`;
  return `${facts.name} (${facts.mapNote?.trim() ?? ''})`;
}

// ----------------------------------------------------------------------------
// "Kept out".
// ----------------------------------------------------------------------------

/**
 * The car keeps robots out although it says it could take them: the
 * adapter has closed its lanes while `commandable` is true, or while
 * `commandable` is not reported and the site is armed (X5; contract
 * section 2). `commandable === false` is never "kept out": the car itself
 * is not taking robot calls, and the line is the staff-setting one.
 */
export function liftKeepsRobotsOut(
  session: Pick<LiftSessionView, 'lanes_closed' | 'commandable'> | null | undefined,
  armed: boolean,
): boolean {
  if (session?.lanes_closed !== true) return false;
  return session.commandable === true || (session.commandable == null && armed);
}

// ----------------------------------------------------------------------------
// Times.
// ----------------------------------------------------------------------------

/**
 * Epoch seconds from the adapter or connector, as epoch ms, or null.
 *
 * Anything at or before 1e9 s (2001) is refused: the on-prem edge has
 * published MONOTONIC seconds in neighbouring fields (`wait.deadline_ts`
 * on edge <= 0.7.3), and a monotonic value rendered as a clock time is a
 * time that never happened.
 */
export function epochSecondsToMs(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 1e9) return null;
  return value * 1000;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MS_PER_DAY = 86_400_000;

/**
 * A stamp as a time of day in the site's zone: "13:58" when it falls on
 * today's date there, "yesterday 13:58" on the date before, otherwise
 * "16 Sep 13:58" ("16 Sep 2025 13:58" in another year). The device's
 * zone is used, marked "(device time)", when the site's is not known.
 * Without the day, a closure that crossed midnight (the 2026-09-16 one
 * lasted about 26 hours) would read the next morning as this afternoon.
 *
 * `wallNowMs` is this device's wall clock (`Date.now()`), read ONLY to
 * tell which calendar date it is in that zone. It is never subtracted
 * from the stamp, so no age comes out of two machines' clocks; a device
 * clock N minutes out can mis-name the day only for a stamp within N
 * minutes of midnight. A non-finite `wallNowMs` always names the date.
 * The caller re-reads at least once a minute (W3's minute tick), so
 * "today" rolls over at midnight.
 */
export function formatClock(
  epochMs: number,
  timeZone: string | null | undefined,
  wallNowMs: number,
): string {
  const known = typeof timeZone === 'string' && timeZone !== '' && isKnownTimeZone(timeZone);
  const zone = known ? timeZone : browserTimeZone();
  const at = zonedParts(epochMs, zone);
  const hhmm = `${String(at.hour).padStart(2, '0')}:${String(at.minute).padStart(2, '0')}`;
  let when = `${at.day} ${MONTHS[at.month - 1]} ${hhmm}`;
  if (Number.isFinite(wallNowMs)) {
    const today = zonedParts(wallNowMs, zone);
    // Calendar dates, compared as UTC midnights: no zone offset or DST
    // step enters the difference.
    const days =
      (Date.UTC(today.year, today.month - 1, today.day) - Date.UTC(at.year, at.month - 1, at.day)) /
      MS_PER_DAY;
    if (days === 0) when = hhmm;
    else if (days === 1) when = `yesterday ${hhmm}`;
    else if (at.year !== today.year) when = `${at.day} ${MONTHS[at.month - 1]} ${at.year} ${hhmm}`;
  }
  return known ? when : `${when} (device time)`;
}

/** "Level 9" for "L9", "Level 4M" for "L4M"; any other name as it is. */
export function levelWords(level: string): string {
  const m = /^L(\d+[A-Z]*)$/.exec(level);
  return m ? `Level ${m[1]}` : level;
}

/** The robot's own name from a session owner id (`<fleet>/<robot>`). */
export function robotName(ownerId: string): string {
  const slash = ownerId.lastIndexOf('/');
  return slash >= 0 ? ownerId.slice(slash + 1) : ownerId;
}

// ----------------------------------------------------------------------------
// The reading.
// ----------------------------------------------------------------------------

export type LiftReadingKind =
  | 'no_reading'
  | 'checking'
  | 'fire_service'
  /** The site's write gate cannot be read. */
  | 'cant_check'
  /** The site's write gate is disarmed or not validated. */
  | 'control_off'
  | 'emergency'
  | 'lift_offline'
  | 'kept_out'
  /** The car is not taking robot calls (staff setting, out of service). */
  | 'not_taking_robots'
  /** A standing lift-wide refusal other than the car's mode. */
  | 'refusing'
  /** A robot's session holds the car at a floor. */
  | 'robot_held'
  /** A robot's session has the car but neither waits for it nor holds it
   *  at a floor: riding, getting out, or a phase not known here. */
  | 'robot_in_use'
  | 'robot_waiting'
  | 'operator_held'
  | 'working';

/** Who should act, as far as the lift reading alone says. W5 decides
 *  the band and when an item is carded. */
export type LiftWhoActs = 'nobody' | 'you' | 'nexsoss' | 'lift_company';

export interface LiftReading {
  readonly name: string;
  readonly kind: LiftReadingKind;
  readonly freshness: LiftCarFreshness;
  /** Why the reading is lost, for `no_reading`; null otherwise. */
  readonly lostBecause: LiftLostReason | null;
  readonly guests: string;
  readonly robots: string;
  readonly whoActs: LiftWhoActs;
  readonly whoActsLine: string;
  /** Whether this reading alone warrants an attention item. Never for a
   *  guest car. Timed escalations (a hold carded at 5 min) are W5's. */
  readonly raisesItem: boolean;
  /** The car keeps robots out although it could take them:
   *  `liftKeepsRobotsOut` on an armed site, unless the car's own words
   *  say it is not taking robot calls while `commandable` is not
   *  reported (see `liftReading`). Set whatever the Robots line says: a
   *  guest car's still reads "Not a robot lift". */
  readonly keptOut: boolean;
  /** Whether a robot could use the car now, as far as this reading
   *  shows: the Robots line, as a value. The site row reads it for the
   *  between-floors cars. */
  readonly robotAccess: 'allowed' | 'blocked' | 'unknown';
  /** Epoch ms of the start the lines cite, from a field with its own
   *  stamp (or the row's last stamp, for "No reading since"). */
  readonly sinceEpochMs: number | null;
  /** Ops only: the robot map still routes robots through this car. */
  readonly opsNote: string | null;
  readonly mode: LiftModeLabelReading;
}

export interface LiftReadingInput {
  readonly facts: LiftRobotFacts;
  /** The live row; undefined when the car has none. */
  readonly row: LiftViewModel | undefined;
  readonly gate: LiftWriteGate | null | undefined;
  readonly freshness: Pick<LiftFreshness, 'state' | 'lostBecause'>;
  /** The site's zone, for "since HH:MM". */
  readonly timeZone?: string | null;
  /** This device's wall clock (`Date.now()`), only to say "yesterday"
   *  or the date on a stamp from before today (`formatClock`). */
  readonly wallNowMs: number;
}

/** Edge phases in which the car is held for the robot (adapter
 *  `core.py::HELD_PHASES`). */
const HELD_PHASES: ReadonlySet<string> = new Set(['held', 'held_boarding', 'held_egress']);

/** Edge phases before the robot is aboard, the car not yet held: the
 *  robot is waiting for it (adapter `core.py::_OUTSIDE_PHASES`, less
 *  `held_boarding`). */
const WAITING_PHASES: ReadonlySet<string> = new Set([
  'requesting_agv',
  'agv_requested',
  'agv_automatic',
  'calling_car',
  'car_ack',
  'car_processed',
  'car_ready',
]);

/** The edge refuses because the car is not in a mode that takes AGV
 *  calls (adapter `core.py::_MODE_REFUSAL_CODES`). */
const MODE_REFUSAL_CODE = 'LIFT_MODE_NOT_NORMAL';

/**
 * Advisory refusal about ONE robot's ride, not the car: "the car is back
 * in building service and this adapter will not call it for this robot
 * until somebody says the ride resumes". It closes no lanes, and "a car
 * in building service is a perfectly usable car for every OTHER robot"
 * (adapter `core.py::RIDE_LATCH_CODE`). So it never reads as the car
 * turning robots away.
 */
const RIDE_LATCH_CODE = 'RIDE_LATCHED';

/**
 * Read one car.
 *
 * The order follows the ideation table (section 3b) and the dispatch
 * verdict: no reading, checking, then what `liftDispatchStatus` says
 * (fire, the gate, emergency, offline), then the session (not taking
 * robots, kept out, a standing refusal, a robot or operator hold), then
 * working. The robot lines of a car robots are not meant to use read
 * "Not a robot lift" wherever they would describe availability; a robot
 * actually in such a car is still named.
 *
 * Not taking robots comes before kept out for the adapters that do not
 * report `commandable` (before lift-session 0.1.18, and 0.1.17 is the
 * declared rollback target). There X5's fallback (`commandable == null
 * && armed`) would make every closure "kept out", the 2026-09-16 shape
 * included -- a car on Independent Service whose Guests line quotes its
 * staff setting while its Robots line says it could take robots. The
 * car's own words outrank that fallback; `liftKeepsRobotsOut` itself is
 * unchanged (contract note C0 section 2), and once `commandable` is
 * reported it decides alone.
 */
export function liftReading(input: LiftReadingInput): LiftReading {
  const { facts, row, gate, timeZone } = input;
  const freshness = input.freshness.state;
  const robotCar = facts.use !== 'none';
  const session = row?.session ?? null;
  const mode = readLiftModeLabel(session?.mode_label);
  const clock = (ms: number): string => formatClock(ms, timeZone, input.wallNowMs);
  const since = (ms: number | null): string => (ms === null ? '' : ` since ${clock(ms)}`);
  const availability = (line: string): string => (robotCar ? line : 'Not a robot lift');
  const base = {
    name: facts.name,
    freshness,
    lostBecause: null as LiftLostReason | null,
    mode,
    opsNote: hasRobotMapLanes(facts)
      ? `The robot map still allows ${mapLanesPhrase(facts)}.`
      : null,
    keptOut: false,
    sinceEpochMs: null as number | null,
  };
  const nobody = { whoActs: 'nobody' as const, whoActsLine: 'Nobody' };
  const notRobots = { whoActs: 'nobody' as const, whoActsLine: 'Nobody: not a robot lift' };
  /** Who acts on something only a robot car's users care about. */
  const forRobots = (act: { whoActs: LiftWhoActs; whoActsLine: string }) => (robotCar ? act : notRobots);
  const callNexsoss = { whoActs: 'nexsoss' as const, whoActsLine: 'Call Nexsoss' };

  const noReading = (): LiftReading => {
    // "Since" only where the row's own last stamp IS the time of the
    // last reading: receipts stopped, or this page's feed clamped. When
    // the connector or adapter says the reading is stale, the worker
    // keeps stamping the row, so its stamp is not when the reading went.
    const lostBecause = input.freshness.lostBecause;
    const stamped = lostBecause === 'no_receipts' || lostBecause === 'feed_clamped';
    const lastMs = stamped ? liftRowStampMs(row) : Number.NaN;
    const lastSeenMs = Number.isFinite(lastMs) ? lastMs : null;
    if (lostBecause === 'feed_clamped') {
      // THIS PAGE's feed is down (an SSE drop, a woken tab, bad Wi-Fi):
      // that says nothing about the lift. No item and nobody to call per
      // car; the one clamp heading ("Reconnecting · last update HH:MM",
      // W3) and W5's evidence tier say it for the whole screen.
      return {
        ...base,
        ...nobody,
        whoActsLine: 'Nobody: this screen is reconnecting',
        lostBecause,
        kind: 'no_reading',
        guests:
          lastSeenMs === null
            ? LIFT_NO_READING_WORDS.reconnecting
            : `${LIFT_NO_READING_WORDS.reconnecting} (last update ${clock(lastSeenMs)})`,
        robots: availability(LIFT_NO_READING_WORDS.reconnecting),
        raisesItem: false,
        robotAccess: 'unknown',
        sinceEpochMs: lastSeenMs,
      };
    }
    const staleAtSource = lostBecause === 'telemetry_stale' || lostBecause === 'reading_age';
    return {
      ...base,
      ...forRobots(callNexsoss),
      lostBecause,
      kind: 'no_reading',
      guests: liftNoReadingLine(lostBecause, row, clock),
      robots: availability(
        staleAtSource ? LIFT_NO_READING_WORDS.noFreshReading : LIFT_NO_READING_WORDS.noReading,
      ),
      raisesItem: robotCar,
      robotAccess: 'unknown',
      sinceEpochMs: lastSeenMs,
    };
  };

  if (freshness === 'absent' || freshness === 'lost' || !row) return noReading();

  if (freshness === 'checking') {
    return {
      ...base,
      ...nobody,
      kind: 'checking',
      guests: 'Checking',
      robots: availability('Checking'),
      raisesItem: false,
      robotAccess: 'unknown',
    };
  }

  // A reading this page trusts (live, quiet or off).
  const dispatch = liftDispatchStatus(row, gate);
  const labelled = mode.modeClass !== 'not_reported';
  switch (dispatch.kind) {
    case 'no_state':
      // Unreachable: `absent` already covers a registry-only row. Spelled
      // out so it can never fall through to "working".
      return noReading();
    case 'fire_service':
      return {
        ...base,
        kind: 'fire_service',
        guests: 'Fire service',
        robots: availability('Not available'),
        whoActs: 'nobody',
        whoActsLine: 'Nobody: the fire panel has it',
        raisesItem: false,
        robotAccess: 'blocked',
      };
    case 'unknown':
    case 'unsupported':
      // The site row says this once; no card per car.
      return {
        ...base,
        ...nobody,
        kind: 'cant_check',
        guests: liftModeGuestsLine(mode),
        robots: availability('Can’t check: lift control status can’t be read'),
        raisesItem: false,
        robotAccess: 'unknown',
      };
    case 'unvalidated':
    case 'disarmed':
      // One site row, not a card per car. A disarmed site publishes mode
      // off for every car whether its edge link is up or down (the
      // connector folds link and gate into `offline`), and the connector
      // keeps sending the edge's RETAINED label while the bridge is
      // offline (`translator.py::protocol_state`). So the car's own
      // words are quoted only while the connector vouches that its
      // telemetry is fresh (`telemetry_fresh === true`, lift-session
      // 0.1.18); before that, or when it is not reported, the ideation's
      // "Not known while lift control is off".
      return {
        ...base,
        ...forRobots(callNexsoss),
        kind: 'control_off',
        guests:
          labelled && session?.telemetry_fresh === true
            ? liftModeGuestsLine(mode)
            : 'Not known while lift control is off',
        robots: availability('Not while lift control is off'),
        raisesItem: false,
        robotAccess: 'blocked',
      };
    case 'emergency':
      return {
        ...base,
        kind: 'emergency',
        guests: 'Not working: it reports an emergency mode',
        robots: availability('Not available'),
        whoActs: 'lift_company',
        whoActsLine: 'Call the lift company, then tell Nexsoss',
        raisesItem: robotCar,
        robotAccess: 'blocked',
      };
    case 'lift_offline':
      return {
        ...base,
        ...forRobots(callNexsoss),
        kind: 'lift_offline',
        guests: 'Out of service, or its link is down',
        robots: availability('Not available'),
        raisesItem: robotCar,
        robotAccess: 'blocked',
      };
    case 'armed':
      break;
  }

  // Armed and validated, the car in human, agv or an unreported mode.
  const s = session;
  const refusal = s?.refusal ?? null;
  const guestsByMode = liftModeGuestsLine(mode);

  // The car is not taking robot calls: it says so (`commandable` false),
  // or, before the field is reported, its own words say so (a staff or
  // out-of-service setting), or the edge's mode refusal does and the car
  // does not say Normal Operation. A mode refusal standing while the car
  // says Normal Operation is the 2026-09-16 replay (the car came back,
  // the closure stood): that is kept out or turning robots away, never
  // "not taking". NOT while the car is in an AGV mode with `commandable`
  // false: 97 ("AGV1 - AGV Request") is the handover every robot ride
  // passes through, and the connector reports it not commandable
  // (`translator.py`) because a second request into it is not answered
  // -- not because the car is closed to robots.
  const carSaysNotTaking = mode.modeClass === 'staff' || mode.modeClass === 'not_in_service';
  const notTaking =
    (s?.commandable === false && mode.modeClass !== 'robot') ||
    (s?.commandable == null &&
      (carSaysNotTaking || (refusal?.code === MODE_REFUSAL_CODE && mode.modeClass !== 'normal')));
  if (notTaking) {
    const blocked = s?.lanes_closed === true || (refusal !== null && refusal.code !== RIDE_LATCH_CODE);
    const startMs =
      refusal?.code === MODE_REFUSAL_CODE
        ? epochSecondsToMs(refusal.first_since_epoch_ts)
        : s?.lanes_closed === true
          ? epochSecondsToMs(s.lanes_closed_since_epoch_ts)
          : null;
    const act =
      mode.modeClass === 'staff'
        ? {
            whoActs: 'you' as const,
            whoActsLine: `You: check whether engineering or a lift technician has ${facts.name} on its key switch`,
          }
        : mode.modeClass === 'not_in_service'
          ? { whoActs: 'lift_company' as const, whoActsLine: 'Call the lift company, then tell Nexsoss' }
          : callNexsoss;
    return {
      ...base,
      ...forRobots(act),
      kind: 'not_taking_robots',
      guests: guestsByMode,
      robots: availability(
        mode.modeClass === 'staff'
          ? 'Can’t board until it is handed back'
          : mode.modeClass === 'not_in_service'
            ? 'Not available'
            : 'Can’t board: the car is not taking robot calls',
      ),
      raisesItem: robotCar && blocked,
      robotAccess: 'blocked',
      sinceEpochMs: startMs,
    };
  }

  if (liftKeepsRobotsOut(s, true)) {
    const closedSince = epochSecondsToMs(s?.lanes_closed_since_epoch_ts);
    return {
      ...base,
      ...forRobots(callNexsoss),
      kind: 'kept_out',
      guests: guestsByMode,
      robots: availability(
        closedSince === null ? 'Kept out (time not reported)' : `Kept out${since(closedSince)}`,
      ),
      raisesItem: robotCar,
      keptOut: true,
      robotAccess: 'blocked',
      sinceEpochMs: closedSince,
    };
  }

  if (refusal !== null && refusal.code !== RIDE_LATCH_CODE) {
    const firstMs = epochSecondsToMs(refusal.first_since_epoch_ts);
    const settled = refusal.escalated === true || refusal.permanent === true;
    return {
      ...base,
      ...forRobots(
        settled ? callNexsoss : { whoActs: 'nobody', whoActsLine: 'Nobody yet: it may clear by itself' },
      ),
      kind: 'refusing',
      guests: guestsByMode,
      robots: availability(
        firstMs === null
          ? 'Turning robots away (time not reported)'
          : `Turning robots away${since(firstMs)}`,
      ),
      raisesItem: robotCar && settled,
      robotAccess: 'blocked',
      sinceEpochMs: firstMs,
    };
  }

  if (s?.active === true && s.owner?.kind === 'robot') {
    const robot = robotName(s.owner.id);
    const phase = s.phase ?? '';
    const startedMs = epochSecondsToMs(s.wait?.started_epoch_ts);
    if (WAITING_PHASES.has(phase)) {
      return {
        ...base,
        ...nobody,
        whoActsLine: 'Nobody yet',
        kind: 'robot_waiting',
        guests: guestsByMode,
        robots: `${robot} waiting for it${since(startedMs)}`,
        raisesItem: false,
        robotAccess: 'allowed',
        sinceEpochMs: startedMs,
      };
    }
    if (HELD_PHASES.has(phase)) {
      const where = row.currentLevel ? ` on ${levelWords(row.currentLevel)}` : '';
      return {
        ...base,
        ...nobody,
        whoActsLine: 'Nobody yet',
        kind: 'robot_held',
        guests: `Held for ${robot}${where}${since(startedMs)}`,
        robots: `In use by ${robot}`,
        raisesItem: false,
        robotAccess: 'allowed',
        sinceEpochMs: startedMs,
      };
    }
    // Carrying the robot (adapter `_INSIDE_PHASES` less `held_egress`:
    // calling_destination, dest_ack, dest_processed, egress) or a phase
    // this file does not know. The car may be moving, so no floor (a car
    // is never placed between floors) and no direction; and no time, as
    // the wait stamp here starts the current step, not the ride.
    return {
      ...base,
      ...nobody,
      whoActsLine: 'Nobody yet',
      kind: 'robot_in_use',
      guests: `In use by ${robot}`,
      robots: `In use by ${robot}`,
      raisesItem: false,
      robotAccess: 'allowed',
    };
  }

  if (s?.active === true && s.owner?.kind === 'operator') {
    // An operator's hold ends by itself (`auto_close_epoch_ts`), so it is
    // not a reason robots cannot use the car.
    return {
      ...base,
      ...nobody,
      kind: 'operator_held',
      guests: 'Held by an operator',
      robots: availability('Held by an operator'),
      raisesItem: false,
      robotAccess: 'allowed',
    };
  }

  return {
    ...base,
    ...nobody,
    kind: 'working',
    guests: guestsByMode,
    robots:
      facts.use === 'between_floors'
        ? 'Allowed'
        : facts.use === 'same_floor'
          ? 'Same floor only'
          : 'Not a robot lift',
    raisesItem: false,
    robotAccess: 'allowed',
  };
}

// ----------------------------------------------------------------------------
// The site row.
// ----------------------------------------------------------------------------

export type LiftSiteRowKind =
  | 'fire'
  | 'lift_control_off'
  | 'cant_check'
  | 'cant_change_floors'
  | 'cant_tell';

export interface LiftSiteRow {
  readonly kind: LiftSiteRowKind;
  /** The row, for every audience. */
  readonly text: string;
  /** Ops only (Details): what the FOH row leaves out. */
  readonly opsNote: string | null;
  /** The cars the row is about. */
  readonly lifts: readonly string[];
  /** `cant_change_floors` only: true while a car robots are not meant to
   *  use still has robot map lanes, so the row names the car instead of
   *  claiming robots cannot change floors at all. */
  readonly hedged: boolean;
}

export interface LiftSiteRowInput {
  /** The platform's fire mode is active (`FireModeState.active`). */
  readonly siteFire: boolean;
  readonly gate: LiftWriteGate | null | undefined;
  /** Every car the site has, robot cars or not, with its reading. */
  readonly cars: readonly { readonly facts: LiftRobotFacts; readonly reading: LiftReading }[];
  readonly timeZone?: string | null;
  /** This device's wall clock, as for `LiftReadingInput.wallNowMs`. */
  readonly wallNowMs: number;
}

/** "SL1", "SL1 or SL2", "SL1, SL2 or SL3". */
function orList(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/**
 * The one site-wide lift row, or null when there is nothing to say.
 *
 * Precedence: fire > lift control off > can't check > can't change
 * floors > can't tell. Only `between_floors` cars count for the last
 * two: PL3 going down strands nobody between floors, so it never makes
 * this row (its own card says what it needs to). "Can't change floors"
 * needs EVERY between-floors car definitely unavailable; any car with no
 * trustworthy reading makes it "can't tell" instead (X4), and any car
 * robots can use makes no row at all.
 */
export function liftSiteRow(input: LiftSiteRowInput): LiftSiteRow | null {
  const { siteFire, gate, cars, timeZone, wallNowMs } = input;
  const row = (
    kind: LiftSiteRowKind,
    text: string,
    opsNote: string | null,
    lifts: readonly string[] = [],
    hedged = false,
  ): LiftSiteRow => ({ kind, text, opsNote, lifts, hedged });

  if (siteFire) {
    // Backed by the adapter: it declines every robot open during a
    // platform fire (`core.py::PLATFORM_FIRE_CODE`, FIRE_EVACUATION).
    return row(
      'fire',
      'Fire alarm: robots can’t use the lifts until it is cleared',
      'The lift-session adapter declines robot lift calls while platform fire mode is active.',
    );
  }

  const gateStatus = writeGateStatus(gate);
  if (gateStatus.kind === 'disarmed' || gateStatus.kind === 'unvalidated') {
    return row(
      'lift_control_off',
      'Robots can’t use any lift: lift control is off',
      gateStatus.kind === 'disarmed'
        ? 'Lift writes are not armed for this site.'
        : 'The lift bridge failed validation and refuses every write.',
    );
  }
  if (gateStatus.kind === 'unknown' || gateStatus.kind === 'unsupported') {
    return row(
      'cant_check',
      'Can’t check whether robots can use the lifts: lift control status can’t be read',
      gateStatus.kind === 'unsupported'
        ? 'This api-server does not publish the lift write gate.'
        : 'The lift write gate could not be read.',
    );
  }

  const between = cars.filter((c) => c.facts.use === 'between_floors');
  if (between.length === 0) return null;
  if (between.some((c) => c.reading.robotAccess === 'allowed')) return null;

  const unknown = between.filter((c) => c.reading.robotAccess !== 'blocked');
  if (unknown.length > 0) {
    const why = unknown.map(({ facts, reading }) => {
      if (reading.kind === 'checking') return `checking ${facts.name}`;
      // This page's own feed, not the car: never "no reading from SL1".
      if (reading.lostBecause === 'feed_clamped') return 'this screen is reconnecting';
      if (reading.lostBecause === 'telemetry_stale' || reading.lostBecause === 'reading_age') {
        return `no fresh reading from ${facts.name}`;
      }
      const since =
        reading.sinceEpochMs === null ? '' : ` since ${formatClock(reading.sinceEpochMs, timeZone, wallNowMs)}`;
      return `no reading from ${facts.name}${since}`;
    });
    return row(
      'cant_tell',
      `Can’t tell whether robots can change floors (${[...new Set(why)].join('; ')})`,
      null,
      unknown.map((c) => c.facts.name),
    );
  }

  const names = between.map((c) => c.facts.name);
  const routes = cars.filter((c) => hasRobotMapLanes(c.facts));
  if (routes.length === 0) {
    return row('cant_change_floors', 'Robots can’t change floors', null, names);
  }
  return row(
    'cant_change_floors',
    `Robots can’t use ${orList(names)}, their ${names.length === 1 ? 'lift' : 'lifts'} between floors`,
    `The robot map still allows ${routes.map((c) => mapLanesPhrase(c.facts)).join(' and ')}.`,
    names,
    true,
  );
}
